import { Filter } from 'bad-words';

// Initialize the bad-words filter
const filter = new Filter();

// Add custom blocked terms that bad-words might miss
// These are normalized versions (lowercase, no spaces)
const customBlockedTerms = [
  // Racial slurs and substring patterns (catches compound words like "snownigga")
  'nigg', 'n1gg', 'niga', 'n1ga', 'neighgar',
  'ch1nk', 'chink',
  'fagg', 'f4gg',
  'retard', 'r3tard',
  'spic', 'sp1c',
  'kike', 'k1ke',
  'coon', 'c00n',
  'tranny', 'tr4nny',
  // Role impersonation
  'admin', 'administrator', 'moderator', 'mod', 'support',
  'official', 'system', 'owner', 'staff', 'developer', 'dev',
  // Misleading usernames
  'clickmania', 'click_mania', 'game', 'server',
];

// Add custom words to the filter
filter.addWords(...customBlockedTerms);

/**
 * Normalizes a username for checking against blocked terms
 * Removes spaces, converts leetspeak, etc.
 */
function normalizeUsername(name) {
  return name
    .toLowerCase()
    .replace(/[\s\W_]+/g, '') // Remove spaces, punctuation, underscores
    .replace(/0/g, 'o')
    .replace(/1/g, 'i')
    .replace(/3/g, 'e')
    .replace(/4/g, 'a')
    .replace(/5/g, 's')
    .replace(/7/g, 't')
    .replace(/8/g, 'b')
    .replace(/[@]/g, 'a')
    .replace(/[€]/g, 'e')
    .replace(/[\$]/g, 's');
}

/**
 * Validates a username against multiple criteria
 * @param {string} username - The username to validate
 * @returns {object} - { valid: boolean, reason?: string }
 */
export function validateUsername(username) {
  // Check if empty or just whitespace
  if (!username || !username.trim()) {
    return { valid: false, reason: 'Username cannot be empty' };
  }

  const trimmed = username.trim();
  
  // Check length (3-20 characters)
  if (trimmed.length < 3) {
    return { valid: false, reason: 'Username must be at least 3 characters' };
  }
  
  if (trimmed.length > 20) {
    return { valid: false, reason: 'Username must be 20 characters or less' };
  }

  // Check for allowed characters (letters, numbers, underscores, hyphens)
  if (!/^[a-zA-Z0-9_\-\s]+$/.test(trimmed)) {
    return { valid: false, reason: 'Username can only contain letters, numbers, spaces, underscores, and hyphens' };
  }

  // Normalize for profanity checking
  const normalized = normalizeUsername(trimmed);
  
  // Check against bad-words filter (includes our custom terms)
  if (filter.isProfane(normalized)) {
    return { valid: false, reason: "Really? C'mon... 🙄 Try being more creative (and less offensive)" };
  }

  // Additional check for variations that might slip through
  // Check if normalized version contains any blocked terms as substrings
  for (const blocked of customBlockedTerms) {
    if (normalized.includes(blocked)) {
      return { valid: false, reason: "Nice try, but nope! 🚫 We see what you did there..." };
    }
  }

  // Check for misleading formats (e.g., "admin_123", "mod-user")
  const rolePatterns = [
    /^(admin|mod|moderator|support|staff|dev|developer|owner|official|system)/i,
    /(admin|mod|moderator|support|staff|dev|developer|owner|official|system)$/i,
  ];
  
  for (const pattern of rolePatterns) {
    if (pattern.test(trimmed)) {
      return { valid: false, reason: "Whoa there, you're not the admin! 👮 Pick something else" };
    }
  }

  // All checks passed
  return { valid: true };
}

/**
 * Sanitizes a username for display (doesn't validate, just cleans)
 * @param {string} username - The username to sanitize
 * @returns {string} - Sanitized username
 */
export function sanitizeUsername(username) {
  if (!username) return '';
  
  // Trim and limit length
  let sanitized = username.trim().slice(0, 20);
  
  // Remove any non-allowed characters
  sanitized = sanitized.replace(/[^a-zA-Z0-9_\-\s]/g, '');
  
  return sanitized;
}